import { CaretDownIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { ExplanationGroup } from '@entities/search-run';
import type { ExplanationItem } from '@entities/search-run';
import type { SelectionBasis as SelectionBasisData } from '@entities/selection';

import styles from './SelectionBasis.module.css';

interface SelectionBasisProps {
  basis: SelectionBasisData;
  currency: string;
}

/**
 * Обоснование попадания в четвёрку (FE-220, MVP_PRD §34.3).
 *
 * **Обоснования «потому что лучший» не существует.** Продукт требует разбора:
 * две–три сильные стороны и один компромисс — кодами с основаниями, как в §14.
 * Поэтому здесь переиспользуется `ExplanationGroup`: он же рисует раскрытие до
 * evidence на странице автомобиля, и два разных вида одного и того же вывода
 * неизбежно разошлись бы.
 *
 * Данные — **снимок на момент отбора** (`selection_basis`), а не текущий
 * пересчёт: обоснование обязано соответствовать тому, что видел пользователь.
 */
export function SelectionBasis({ basis, currency }: SelectionBasisProps) {
  const { t } = useTranslation();

  const strengths = (basis.strengths ?? []) as ExplanationItem[];
  const compromise = basis.compromise as ExplanationItem | null | undefined;
  const compromises = compromise ? [compromise] : [];

  if (strengths.length === 0 && compromises.length === 0) return null;

  return (
    <details className={styles.details}>
      <summary className={styles.summary}>
        <span>{t('selection.basis.title')}</span>
        <CaretDownIcon size={16} aria-hidden="true" className={styles.caret} />
      </summary>

      <div className={styles.body}>
        {strengths.length > 0 ? (
          <ExplanationGroup kind="advantages" items={strengths} currency={currency} />
        ) : null}
        {compromises.length > 0 ? (
          <ExplanationGroup kind="compromises" items={compromises} currency={currency} />
        ) : null}

        {/* Правило разнообразия — часть отбора, и его снятие объясняется:
            иначе два автомобиля одной модели в четвёрке выглядят ошибкой. */}
        {basis.diversity_rule_applied === false ? (
          <p className={styles.note}>{t('selection.basis.diversityLifted')}</p>
        ) : null}
      </div>
    </details>
  );
}
